import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import Settings from "../../entities/Settings";
import i18n from "../../i18n";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import SliceSingletonBase from "../types/SliceSingletonBase";
import { authHeader, getWorkerId } from "../users/auth";
import WorkerSettings from "../../entities/WorkerSettings";
import DmsSetting from "entities/DmsSetting";
import { extractCollection, extractFirst } from "./normalizers";


// RESPONSE DECLARATIONS //
interface SettingsResponse extends ResponseBase {
  AbsenceAdvancePaymentLockThreshold?: number | null;
  AbsenceApplicationLockThreshold?: number | null;
  AbsenceReminderEnabled: boolean;
  AdditionalPersonDataEnabled: boolean;
  AdditionalPersonDataStructure?: string;
  AgreementsEnabled: boolean;
  ApproverSelectionEnabled: boolean;
  ChildDataEditEnabled: boolean;
  CompanyId: number;
  CompanyWideSubstitutes: boolean;
  CompuAccessClientId: string;
  CompuAccessClientSecret: string;
  DefaultPayslipHeaderId: number;
  EmergencyContactEditEnabled: boolean;
  GlobalCalendarEnabled: boolean;
  ManagerAbsenceAdditionEnabled: boolean;
  PayrollInfoEnabled: boolean;
  PensionerTaxFreeAmount: number;
  SubstituteSelectionEnabled: boolean;
  TaxFreeAmount: number;
  RoundUpUnplannedVacationBalance:boolean;
}

interface WorkerSettingsResponse extends ResponseBase {
  WorkerId: number;
  SelectedContractId?: number;
  SelectedContractPositionId?: number;
}

// SLICE //
interface State extends SliceCollectionBase<Settings> {
  forCompany: SliceSingletonBase<Settings>;
  forTenant: SliceSingletonBase<Settings>;
  workerSettings: SliceSingletonBase<WorkerSettings>;
  dmsSettings: SliceSingletonBase<DmsSetting>;
}

const initialState: State = {
  list: [],
  loading: false,
  lastFetch: null,
  error: null,
  forCompany: {
    data: null,
    loading: false,
    lastFetch: null,
    error: null
  },
  forTenant: {
    data: null,
    loading: false,
    lastFetch: null,
    error: null
  },
  workerSettings: {
    data: null,
    loading: false,
    lastFetch: null,
    error: null
  },
    dmsSettings: {
        data: null,
        loading: false,
        lastFetch: null,
        error: null
    }
};

const slice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    // ALL SETTINGS
    settingsRequested: settings => {
      settings.loading = true;
      settings.error = null;
    },
    settingsReceived: (
      settings,
      action: PayloadAction<unknown>
    ) => {
      settings.list = settingsListSchema(extractCollection<SettingsResponse>(action.payload));
      settings.loading = false;
      settings.lastFetch = Date.now();
    },
    settingsRequestFailed: (
      settings,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      settings.loading = false;
      settings.error = action.payload;
    },

    // COMPANY SETTINGS
    companySettingsRequested: settings => {
      settings.loading = true;
      settings.error = null;
    },
    companySettingsReceived: (
      settings,
      action: PayloadAction<unknown>
    ) => {
      settings.forCompany.data = settingSchema(extractFirst<SettingsResponse>(action.payload));
      settings.forCompany.loading = false;
      settings.forCompany.lastFetch = Date.now();
    },
    companySettingsRequestFailed: (settings, action) => {
      settings.forCompany.loading = false;
      settings.forCompany.error = action.payload;
    },

    // TENANT SETTINGS
    tenantSettingsRequested: settings => {
      settings.forTenant.loading = true;
      settings.forTenant.error = null;
    },
    tenantSettingsReceived: (
      settings,
      action: PayloadAction<unknown>
    ) => {
      settings.forTenant.data = settingSchema(extractFirst<SettingsResponse>(action.payload));
      settings.forTenant.loading = false;
      settings.forTenant.lastFetch = Date.now();
    },
    tenantSettingsRequestFailed: (settings, action) => {
      settings.forTenant.loading = false;
      settings.forTenant.error = action.payload;
    },

    // WORKER SETTINGS
    workerSettingsRequested: settings => {
      settings.workerSettings.loading = true;
      settings.workerSettings.error = null;
    },
    workerSettingsReceived: (
      settings,
      action: PayloadAction<unknown>
    ) => {
      settings.workerSettings.data = workerSettingsSchema(extractCollection<WorkerSettingsResponse>(action.payload));
      settings.workerSettings.loading = false;
      settings.workerSettings.lastFetch = Date.now();
    },
    workerSettingsRequestFailed: (settings, action) => {
      settings.workerSettings.loading = false;
      settings.workerSettings.error = action.payload;
    },

    //DMS SETTINGS
    dmsSettingsRequested: settings => {
        settings.dmsSettings.loading = true;
        settings.dmsSettings.error = null;
    },
    dmsSettingsReceived: (settings, action: PayloadAction<DmsSetting>) => {
        settings.dmsSettings.data = action.payload;
        settings.dmsSettings.loading = false;
        settings.dmsSettings.lastFetch = Date.now();
    },
    dmsSettingsRequestFailed: (settings, action) => {
        settings.dmsSettings.loading = false;
        settings.dmsSettings.error = action.payload;
    },
    dmsSettingsUpdateRequested: settings => {
        settings.dmsSettings.loading = true;
        settings.dmsSettings.error = null;
    },
    dmsSettingsUpdateSucceeded: (settings, action: PayloadAction<DmsSetting>) => {
        settings.dmsSettings.data = action.payload;
        settings.dmsSettings.loading = false;
        settings.dmsSettings.lastFetch = Date.now();
    },
    dmsSettingsUpdateRequestFailed: (settings, action) => {
        settings.dmsSettings.loading = false;
        settings.dmsSettings.error = action.payload;
    }
  }
});

const {
  settingsRequested,
  settingsReceived,
  settingsRequestFailed,

  companySettingsRequested,
  companySettingsReceived,
  companySettingsRequestFailed,

  tenantSettingsRequested,
  tenantSettingsReceived,
  tenantSettingsRequestFailed,

  workerSettingsRequested,
  workerSettingsReceived,
  workerSettingsRequestFailed,

  dmsSettingsRequested,
  dmsSettingsReceived,
  dmsSettingsRequestFailed,

  dmsSettingsUpdateRequested,
  dmsSettingsUpdateSucceeded,
  dmsSettingsUpdateRequestFailed,
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getAllSettings =
  () => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.settings.loading) {
      return;
    }
    const url = `/api/classificator/roof-settings?orderBy=CompanyId&descending=true`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: settingsRequested.type,
        onSuccess: settingsReceived.type,
        onError: settingsRequestFailed.type
      })
    );
  };

export const getCompanySettings =
  (companyId?: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.settings.forCompany.loading) {
      return;
    }

        if (companyId === undefined) {
            companyId = getState().users.auth.data?.CompanyId ?? 0;
        }
        const url = `/api/classificator/roof-settings?companyId=${companyId}&includeDefault=true&take=1&orderBy=CompanyId&descending=true`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: companySettingsRequested.type,
        onSuccess: companySettingsReceived.type,
        onError: companySettingsRequestFailed.type
      })
    );
  };

export const getTenantSettings =
  () => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.settings.forCompany.loading) {
      return;
    }

    const { isLoggedIn } = getState().users.auth;
    if (!isLoggedIn) {
      return;
    }

    const url = `/api/Tenant/GetRoofSettings`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: tenantSettingsRequested.type,
        onSuccess: tenantSettingsReceived.type,
        onError: tenantSettingsRequestFailed.type
      })
    );
  };

export const postCompanySettings =
  (settings: Settings) =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    const url = `/api/tenant/UpdateRoofSettings`;
    const updatedSettings = {...settings, AdditionalPersonDataStructure: JSON.stringify(settings.AdditionalPersonDataStructure)};
  
    return await dispatch(
      apiCallBegan({
        url,
        method: "POST",
        data: updatedSettings,
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        }
      })
    );
  };

export const getWorkerSettings =
  () => async (dispatch: AppDispatch, getState: () => AppState) => {
    const workerId = getWorkerId(getState());
    if (!workerId || getState().entities.settings.workerSettings.loading) {
      return;
    }

    const url = `/api/classificator/worker-settings?workerId=${workerId}`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: workerSettingsRequested.type,
        onSuccess: workerSettingsReceived.type,
        onError: workerSettingsRequestFailed.type
      })
    );
  };

export const getDmsSettings = (tenantId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
    const url = `/api/Roof/dmsSettings/${tenantId}`;
    return await dispatch(
        apiCallBegan({
            url,
            method: "GET",
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: dmsSettingsRequested.type,
            onSuccess: dmsSettingsReceived.type,
            onError: dmsSettingsRequestFailed.type
        })
    );
};

export const updateDmsSettings = (tenantId: number, settings: DmsSetting) => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!settings) {
        return;
    }

    const url = `/api/Roof/dmsSettings/${tenantId}`;
    return await dispatch(
        apiCallBegan({
            url,
            method: "PATCH",
            data: settings,
            headers: { ...authHeader(), "Accept-Language": i18n.language },
            onStart: dmsSettingsUpdateRequested.type,
            onSuccess: dmsSettingsUpdateSucceeded.type,
            onError: dmsSettingsUpdateRequestFailed.type
        })
    );
  };

// NORMALIZATION //
const settingSchema = (resp: SettingsResponse | null): Settings | null => {
  if (!resp) {
    return null;
  }
  return {
    AbsenceAdvancePaymentLockThreshold:
      resp.AbsenceAdvancePaymentLockThreshold ?? undefined,
    AbsenceApplicationLockThreshold:
      resp.AbsenceApplicationLockThreshold ?? undefined,
    AbsenceReminderEnabled: resp.AbsenceReminderEnabled,
    AdditionalPersonDataEnabled: resp.AdditionalPersonDataEnabled,
    AdditionalPersonDataStructure:
      resp.AdditionalPersonDataStructure &&
      JSON.parse(resp.AdditionalPersonDataStructure),
    AgreementsEnabled: resp.AgreementsEnabled,
    ApproverSelectionEnabled: resp.ApproverSelectionEnabled,
    ChildDataEditEnabled: resp.ChildDataEditEnabled,
    CompanyId: resp.CompanyId,
    CompanyWideSubstitutes: resp.CompanyWideSubstitutes,
    CompuAccessClientId: resp.CompuAccessClientId,
    CompuAccessClientSecret: resp.CompuAccessClientSecret,
    DefaultPayslipHeaderId: resp.DefaultPayslipHeaderId,
    EmergencyContactEditEnabled: resp.EmergencyContactEditEnabled,
    GlobalCalendarEnabled: resp.GlobalCalendarEnabled,
    Id: resp.Id,
    ManagerAbsenceAdditionEnabled: resp.ManagerAbsenceAdditionEnabled,
    PayrollInfoEnabled: resp.PayrollInfoEnabled,
    PensionerTaxFreeAmount: resp.PensionerTaxFreeAmount,
    SubstituteSelectionEnabled: resp.SubstituteSelectionEnabled,
    TaxFreeAmount: resp.TaxFreeAmount,
    RoundUpUnplannedVacationBalance: resp.RoundUpUnplannedVacationBalance
  };
};

const settingsListSchema = (payload: SettingsResponse[]): Settings[] => {
  if (!payload || payload.length === 0) {
    return [];
  }
  return payload
    .filter((s): s is SettingsResponse => s !== null)
    .map((resp: SettingsResponse): Settings | null => settingSchema(resp))
    .filter((setting): setting is Settings => setting !== null);
};

const workerSettingsSchema = (
  resp: WorkerSettingsResponse[]
): WorkerSettings | null => {
  if (!resp || !resp[0]) {
    return null;
  }
  return {
    Id: resp[0].Id,
    SelectedContractId: resp[0].SelectedContractId,
    SelectedContractPositionId: resp[0].SelectedContractPositionId,
    WorkerId: resp[0].WorkerId
  };
};
